import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appointmentService } from '../services/api';
import { Calendar, Clock, CheckCircle, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const PERIODS = [
  { id: '7d',  label: 'Últimos 7 dias',  days: 7 },
  { id: '30d', label: 'Últimos 30 dias', days: 30 },
  { id: '90d', label: 'Últimos 90 dias', days: 90 }
];

const AppointmentStats = () => {
  const [periodId, setPeriodId] = useState('30d');
  const period = useMemo(
    () => PERIODS.find(p => p.id === periodId) || PERIODS[1],
    [periodId]
  );

  // Inclui agendamentos passados e futuros próximos para refletir a realidade da operação.
  const { data: statsData, isLoading, error } = useQuery({
    queryKey: ['appointment-stats', periodId],
    queryFn: () => {
      const now = new Date();
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - period.days);
      const dateTo = new Date();
      dateTo.setDate(dateTo.getDate() + period.days);
      return appointmentService.getAppointmentStats({
        date_from: dateFrom.toISOString(),
        date_to: dateTo.toISOString()
      });
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Estatísticas de Agendamentos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Erro ao carregar estatísticas de agendamentos</p>
      </div>
    );
  }

  // BUG-04 fix: suporte robusto a qualquer envelope Axios ({ data: { stats } } ou { stats })
  const rawStats = statsData?.data?.stats || statsData?.stats || statsData?.data || {};
  const stats = {
    total: parseInt(rawStats.total || 0, 10),
    scheduled: parseInt(rawStats.scheduled || 0, 10),
    in_progress: parseInt(rawStats.in_progress || 0, 10),
    completed: parseInt(rawStats.completed || 0, 10),
    cancelled: parseInt(rawStats.cancelled || 0, 10)
  };

  const statCards = [
    {
      title: 'Total de Agendamentos',
      value: stats.total,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Agendados',
      value: stats.scheduled,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      title: 'Em Andamento',
      value: stats.in_progress,
      icon: AlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      title: 'Concluídos',
      value: stats.completed,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Estatísticas de Agendamentos
          </h2>
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriodId(p.id)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  periodId === p.id
                    ? 'bg-white text-[#49943d] shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => (
            <div key={stat.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Resumo</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-600">Concluídos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-sm text-gray-600">Cancelados</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
            </div>
            <div className="text-sm text-gray-600">Taxa de Conclusão</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentStats;
